import {
  getToolDisplayName,
  getToolOverride,
  getToolValidationAlwaysAllowLabel,
} from "@app/components/actions/blocked/toolValidationLabels";
import { ToolValidationDetails } from "@app/components/assistant/conversation/ToolValidationDetails";
import { getIcon } from "@app/components/resources/resources_icons";
import type { MCPValidationOutputType } from "@app/lib/actions/constants";
import type { BlockedToolExecution } from "@app/lib/actions/mcp";
import { iconeImage } from "@app/lib/actions/mcp_icons";
import { canCurrentUserRespondToParentUserMessage } from "@app/lib/api/assistant/conversation/can_current_user_respond";
import { t as tFr } from "@app/lib/compta/i18n";
import { imageOutilDepuisNomServeur } from "@app/lib/compta/icones_outils";
import { asDisplayName } from "@app/types/shared/utils/string_utils";
import type { LightWorkspaceType, UserType } from "@app/types/user";
import {
  Avatar,
  Button,
  Card,
  Check,
  CheckDouble,
  Dialog,
  DialogContainer,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  PieChart01,
  XClose,
} from "@dust-tt/sparkle";
import { Trans, useLingui } from "@lingui/react/macro";
import { useState } from "react";

const DEFAULT_ICON = PieChart01;

// Display data needed to render a tool validation card, for both agent-loop and sandbox-function
// blocked tool executions.
type ToolValidationRequest = Pick<
  BlockedToolExecution,
  | "actionId"
  | "userId"
  | "stake"
  | "inputs"
  | "metadata"
  | "approvalArgsLabel"
  | "argumentsRequiringApproval"
>;

type ApprovalProgressProps = {
  current: number;
  total: number;
};

function ApprovalProgress({ current, total }: ApprovalProgressProps) {
  if (total <= 1) {
    return null;
  }

  return (
    <div className="heading-xs shrink-0 text-muted-foreground">
      <span className="sr-only">
        <Trans>
          Approval {current} of {total}
        </Trans>
      </span>
      <span aria-hidden="true">
        {current}/{total}
      </span>
    </div>
  );
}

interface ToolValidationCardProps {
  validationRequest: ToolValidationRequest;
  approvalProgress?: ApprovalProgressProps;
  triggeringUser: UserType | null;
  // The viewer looking at the card. Passed in rather than read from `AuthContext` because shared
  // frames render this card outside of any AuthProvider.
  currentUser: UserType;
  owner: LightWorkspaceType;
  conversationId?: string | null;
  // Shown inline under the card, for viewers without notifications (shared frames).
  errorMessage?: string | null;
  isValidating: boolean;
  isPulsing?: boolean;
  // Submits the user's decision; returns whether the submission succeeded.
  onValidate: (approved: MCPValidationOutputType) => Promise<boolean>;
  // When provided, offers an ephemeral "allow for this conversation" option that
  // suppresses further prompts for this tool for the rest of the conversation.
  // Only wired up for low/medium stake tools on surfaces that opt in.
  onApproveForConversation?: () => Promise<boolean>;
}

interface ToolValidationDetailsDialogProps {
  validationRequest: ToolValidationRequest;
  approvalTitle: string;
  displayLabel: string;
  icon: React.ComponentProps<typeof Avatar>["icon"];
  currentUser: UserType;
  owner: LightWorkspaceType;
  conversationId?: string | null;
  isSubmitting: boolean;
}

function ToolValidationDetailsDialog({
  validationRequest,
  approvalTitle,
  displayLabel,
  icon,
  currentUser,
  owner,
  conversationId,
  isSubmitting,
}: ToolValidationDetailsDialogProps) {
  const { t } = useLingui();

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          label={t`Review details`}
          variant="ghost"
          size="sm"
          className="min-h-11 sm:min-h-0"
          disabled={isSubmitting}
        />
      </DialogTrigger>
      <DialogContent
        size="lg"
        className="gap-4 p-5"
        preventAutoFocusOnClose={false}
      >
        <DialogHeader className="static gap-1 p-0">
          <DialogTitle visual={<Avatar icon={icon} size="sm" />}>
            {approvalTitle}
          </DialogTitle>
          <DialogDescription className="pl-11">
            {displayLabel}
          </DialogDescription>
        </DialogHeader>
        <DialogContainer className="max-h-[60vh] overflow-y-auto p-0">
          <ToolValidationDetails
            blockedAction={validationRequest}
            user={currentUser}
            owner={owner}
            conversationId={conversationId}
          />
        </DialogContainer>
      </DialogContent>
    </Dialog>
  );
}

/**
 * @cc [owner:tdraier,label:react;product] conversation-approval-medium-only
 * The "Allow all in this conversation" button MUST be offered only when
 * `onApproveForConversation` is provided AND the tool is `medium` stake AND it
 * declares a non-empty `argumentsRequiringApproval`. That is the only case where
 * "Always allow" keeps re-prompting (per input value); at other stakes the button
 * is redundant (`never_ask`/`low`) or contradicts the "never savable" intent
 * (`high`), and a medium tool with no approval args is already covered
 * permanently by "Always allow".
 */
export function ToolValidationCard({
  validationRequest,
  approvalProgress,
  triggeringUser,
  currentUser,
  owner,
  conversationId,
  errorMessage,
  isValidating,
  isPulsing = false,
  onValidate,
  onApproveForConversation,
}: ToolValidationCardProps) {
  const { t } = useLingui();
  const toolOverride = getToolOverride(validationRequest.metadata);
  const [submittingDecision, setSubmittingDecision] =
    useState<MCPValidationOutputType | null>(null);
  const [isApprovingForConversation, setIsApprovingForConversation] =
    useState(false);
  const isSubmitting =
    isValidating || submittingDecision !== null || isApprovingForConversation;

  const canCurrentUserRespond = canCurrentUserRespondToParentUserMessage({
    parentUserId: validationRequest.userId,
    currentUserId: currentUser.sId,
  });

  // Fork : image de marque des serveurs distants (lib/compta/icones_outils.ts).
  const imageDeMarque = imageOutilDepuisNomServeur(
    validationRequest.metadata.mcpServerName
  );
  const icon = imageDeMarque
    ? iconeImage(imageDeMarque)
    : validationRequest.metadata.icon
      ? getIcon(validationRequest.metadata.icon)
      : undefined;

  const handleValidation = async (approvalState: MCPValidationOutputType) => {
    setSubmittingDecision(approvalState);
    try {
      await onValidate(approvalState);
    } finally {
      setSubmittingDecision(null);
    }
  };

  const handleApproveForConversation = async () => {
    if (!onApproveForConversation) {
      return;
    }
    setIsApprovingForConversation(true);
    try {
      await onApproveForConversation();
    } finally {
      setIsApprovingForConversation(false);
    }
  };

  const {
    metadata: { agentName, mcpServerName },
  } = validationRequest;

  // compta : nom de serveur traduit par notre catalogue (valeur dynamique, hors Lingui).
  const serverName = tFr(asDisplayName(mcpServerName));
  const approvalTitle = t`Allow ${agentName} to use ${serverName}?`;
  // Fork : libellé français d'un outil métier connu (front/lib/compta/libelles_outils.ts).
  const displayLabel =
    validationRequest.metadata.displayLabel ??
    getToolDisplayName(validationRequest.metadata);
  const showDetailsInline =
    canCurrentUserRespond && toolOverride?.detailsInline === true;

  const canAlwaysAllow = ["low", "medium"].includes(
    validationRequest.stake ?? ""
  );

  // "Allow all in this conversation" only adds value for a medium-stake tool
  // that re-prompts per input value. At other stakes it is redundant
  // (never_ask/low) or contradicts "never savable" (high), and a medium tool
  // with no approval args is already covered permanently by "Always allow".
  const canApproveForConversation =
    Boolean(onApproveForConversation) &&
    validationRequest.stake === "medium" &&
    (validationRequest.argumentsRequiringApproval?.length ?? 0) > 0;
  const triggeringUserName = triggeringUser?.fullName;

  return (
    <Card
      variant="secondary"
      containerClassName="w-full max-w-xl"
      className="flex flex-col shadow gap-4"
      isPulsing={isPulsing}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Avatar icon={icon ?? DEFAULT_ICON} size="sm" />
          <div className="heading-base min-w-0">{approvalTitle}</div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {approvalProgress && <ApprovalProgress {...approvalProgress} />}
          {canCurrentUserRespond &&
            !showDetailsInline &&
            Object.keys(validationRequest.inputs).length > 0 && (
              <ToolValidationDetailsDialog
                validationRequest={validationRequest}
                approvalTitle={approvalTitle}
                displayLabel={displayLabel}
                icon={icon ?? DEFAULT_ICON}
                currentUser={currentUser}
                owner={owner}
                conversationId={conversationId}
                isSubmitting={isSubmitting}
              />
            )}
        </div>
      </div>

      <div className="text-base text-muted-foreground">{displayLabel}</div>

      {showDetailsInline && (
        <ToolValidationDetails
          blockedAction={validationRequest}
          user={currentUser}
          owner={owner}
          conversationId={conversationId}
        />
      )}

      {canCurrentUserRespond ? (
        <>
          {errorMessage && (
            <div className="text-sm font-medium text-warning-800">
              {errorMessage}
            </div>
          )}
        </>
      ) : (
        <div className="text-sm text-muted-foreground">
          <Trans>
            Waiting for{" "}
            <span className="font-semibold text-foreground">
              {triggeringUserName}
            </span>{" "}
            to confirm.
          </Trans>
        </div>
      )}

      {canCurrentUserRespond && (
        <div className="flex flex-wrap justify-end gap-3">
          <Button
            label={t`Decline`}
            variant="outline"
            icon={XClose}
            disabled={isSubmitting}
            isLoading={submittingDecision === "rejected"}
            onClick={() => void handleValidation("rejected")}
          />
          {canAlwaysAllow && (
            <Button
              label={t`Always allow`}
              variant="outline"
              icon={CheckDouble}
              tooltip={getToolValidationAlwaysAllowLabel(validationRequest, t)}
              disabled={isSubmitting}
              isLoading={submittingDecision === "always_approved"}
              onClick={() => void handleValidation("always_approved")}
            />
          )}
          {canApproveForConversation && (
            <Button
              label={t`Allow all inputs this session`}
              variant="outline"
              icon={Check}
              tooltip={t`Run this tool for the rest of this session without asking again, whatever the inputs. Nothing is saved afterwards.`}
              disabled={isSubmitting}
              isLoading={isApprovingForConversation}
              onClick={() => void handleApproveForConversation()}
            />
          )}
          <Button
            label={canAlwaysAllow ? t`Allow once` : t`Allow`}
            variant="highlight"
            icon={Check}
            disabled={isSubmitting}
            isLoading={submittingDecision === "approved"}
            onClick={() => void handleValidation("approved")}
          />
        </div>
      )}
    </Card>
  );
}
